import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Images, Plus, X } from "lucide-react";
import { parseBlockContent, type BlockContent } from "../../../lib/blockContentSchemas";
import { assetService } from "../../assets";
import { Button } from "../../../components/ui";
import type { EditorAsset, EditorBlock } from "../editor.types";
import { BottomSheet } from "./BottomSheet";
import { MediaPicker } from "./MediaPicker";
import { MenuTextInput } from "./Menu";
import { onEditCarouselImages, pendingCarouselPicker } from "./carouselEditing";
import { isStarterPhoto, STARTER_PHOTOS, starterPhotoUrl } from "../../../lib/starterPhotos";
import "./CarouselBlock.css";

type Carousel = BlockContent<"carousel">;
type Item = Carousel["items"][number];
const AUTOPLAY_MS = 5000;

/** A slide's picture: an uploaded image, or a starter photograph. */
function imageOf(item: Item, assetsById: Map<string, EditorAsset>) {
  if (item.sample && isStarterPhoto(item.sample))
    return { src: starterPhotoUrl(item.sample), alt: STARTER_PHOTOS[item.sample].alt };
  const asset = item.assetId ? assetsById.get(item.assetId) : undefined;
  return asset ? { src: assetService.getPublicUrl(asset.storage_path), alt: asset.alt_text ?? "" } : null;
}

/**
 * Images to step or swipe through: one at a time, or several in view. On the
 * live site it can move on by itself (pausing while pointed at or focused,
 * and never with reduced motion). In the editor its images are chosen from
 * the site's media and arranged in a sheet.
 */
export function CarouselBlock({
  block,
  assetsById,
  onUpdate,
  onUploadAsset,
}: {
  block: EditorBlock;
  assetsById: Map<string, EditorAsset>;
  /** Only in the editor. */
  onUpdate?: (content: Carousel) => void;
  onUploadAsset?: (file: File) => Promise<EditorAsset>;
}): ReactNode {
  const content = parseBlockContent("carousel", block.content);
  const items = content.items.filter((item) => imageOf(item, assetsById));
  // Just added from the picker: its images open for choosing at once.
  const [picking, setPicking] = useState(() => Boolean(onUpdate && pendingCarouselPicker.has(block.source_block_id)));
  const [managing, setManaging] = useState(false);
  useEffect(() => {
    pendingCarouselPicker.delete(block.source_block_id);
  }, [block.source_block_id]);
  useEffect(() => (onUpdate ? onEditCarouselImages(block.source_block_id, () => setManaging(true)) : undefined), [onUpdate, block.source_block_id]);
  const update = (patch: Partial<Carousel>) => onUpdate?.({ ...content, ...patch });
  const add = (chosen: EditorAsset[]) =>
    update({ items: [...content.items, ...chosen.map((asset) => ({ assetId: asset.id }))].slice(0, 24) });
  const picker = picking && onUpdate && onUploadAsset && (
    <MediaPicker
      multiple
      title="Images for your carousel"
      assets={[...assetsById.values()]}
      onUpload={onUploadAsset}
      onChoose={(chosen) => {
        setPicking(false);
        add(chosen);
      }}
      onClose={() => setPicking(false)}
    />
  );
  // Removing the last image closes "Edit images"; it mustn't reopen later.
  if (!items.length && managing) setManaging(false);
  if (!items.length)
    return onUpdate ? (
      <div className="document-image-placeholder site-carousel-empty">
        <Images size={30} strokeWidth={1.4} aria-hidden="true" />
        <strong>A carousel of images</strong>
        <div>
          <button type="button" onClick={() => setPicking(true)}>
            Choose images
          </button>
        </div>
        <small>Pick from your images, or upload new ones. Visitors swipe or step through them.</small>
        {picker}
      </div>
    ) : null;
  return (
    <figure className="site-carousel" data-layout={content.layout} data-shape={content.shape}>
      <CarouselTrack items={items} assetsById={assetsById} autoplay={content.autoplay && !onUpdate} label={content.caption} />
      {content.caption && <figcaption className="mt-2.5 text-sm text-site-muted">{content.caption}</figcaption>}
      {onUpdate && (
        <button type="button" className="site-carousel-edit" onClick={() => setManaging(true)}>
          <Images size={14} aria-hidden="true" />
          Edit images
        </button>
      )}
      {managing && onUpdate && (
        <CarouselImages
          items={content.items}
          assetsById={assetsById}
          onChange={(next) => update({ items: next })}
          onAdd={() => setPicking(true)}
          onClose={() => setManaging(false)}
        />
      )}
      {picker}
    </figure>
  );
}

/** The row itself, its arrows and its dots. */
function CarouselTrack({
  items,
  assetsById,
  autoplay,
  label,
}: {
  items: Item[];
  assetsById: Map<string, EditorAsset>;
  autoplay: boolean;
  label?: string;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = (index: number) => {
    const element = track.current;
    const slide = element?.children[(index + items.length) % items.length] as HTMLElement | undefined;
    if (!element || !slide) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    element.scrollTo({ left: slide.offsetLeft - element.offsetLeft, behavior: reduce ? "auto" : "smooth" });
  };
  // Which slide is in view, as it scrolls (by arrows, dots, swipes or keys).
  useEffect(() => {
    const element = track.current;
    if (!element) return;
    const measure = () => {
      const slides = [...element.children] as HTMLElement[];
      const left = element.scrollLeft;
      let nearest = 0;
      slides.forEach((slide, index) => {
        if (Math.abs(slide.offsetLeft - element.offsetLeft - left) < Math.abs(slides[nearest].offsetLeft - element.offsetLeft - left)) nearest = index;
      });
      // At the end of a strip, the last one counts as reached.
      if (left + element.clientWidth >= element.scrollWidth - 2) nearest = slides.length - 1;
      setCurrent(nearest);
    };
    element.addEventListener("scroll", measure, { passive: true });
    return () => element.removeEventListener("scroll", measure);
  }, [items.length]);
  useEffect(() => {
    if (!autoplay || paused || items.length < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setTimeout(() => {
      if (document.visibilityState === "visible") go(current + 1);
    }, AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [autoplay, paused, items.length, current]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div
      className="site-carousel-frame"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div
        ref={track}
        className="site-carousel-track"
        role="region"
        aria-roledescription="carousel"
        aria-label={label || "Images"}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
          event.preventDefault();
          go(current + (event.key === "ArrowRight" ? 1 : -1));
        }}
      >
        {items.map((item, index) => {
          const image = imageOf(item, assetsById)!;
          return (
            <figure key={`${item.assetId ?? item.sample}-${index}`} className="site-carousel-slide" aria-roledescription="slide" aria-label={`${index + 1} of ${items.length}`}>
              <img src={image.src} alt={image.alt} loading={index > 1 ? "lazy" : undefined} draggable={false} />
              {item.caption && <figcaption>{item.caption}</figcaption>}
            </figure>
          );
        })}
      </div>
      {items.length > 1 && (
        <div className="site-carousel-controls">
          <button type="button" aria-label="Previous image" onClick={() => go(current - 1)}>
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <div className="site-carousel-dots">
            {items.map((item, index) => (
              <button
                key={`${item.assetId ?? item.sample}-${index}`}
                type="button"
                aria-label={`Show image ${index + 1}`}
                aria-current={index === current || undefined}
                onClick={() => go(index)}
              />
            ))}
          </div>
          <button type="button" aria-label="Next image" onClick={() => go(current + 1)}>
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

/** The carousel's images in order: captions, moving and removing, adding more. */
function CarouselImages({
  items,
  assetsById,
  onChange,
  onAdd,
  onClose,
}: {
  items: Item[];
  assetsById: Map<string, EditorAsset>;
  onChange: (items: Item[]) => void;
  onAdd: () => void;
  onClose: () => void;
}) {
  const move = (from: number, to: number) => {
    const next = [...items];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  return createPortal(
    <BottomSheet open title="Carousel images" titleId="carousel-images-title" onClose={onClose}>
      <ol className="-mx-1 min-h-0 space-y-2 overflow-y-auto px-1 pb-1">
        {items.map((item, index) => {
          const image = imageOf(item, assetsById);
          return (
            <li key={`${item.assetId ?? item.sample}-${index}`} className="flex items-center gap-2.5 rounded-xl border border-hazumu-border p-2">
              {image ? (
                <img src={image.src} alt="" className="size-12 flex-none rounded-lg object-cover" />
              ) : (
                <span className="size-12 flex-none rounded-lg bg-hazumu-canvas" />
              )}
              <MenuTextInput
                aria-label={`Caption for image ${index + 1}`}
                placeholder="Caption (optional)"
                maxLength={200}
                value={item.caption ?? ""}
                onChange={(event) =>
                  onChange(items.map((other, i) => (i === index ? { ...other, caption: event.target.value || undefined } : other)))
                }
              />
              <div className="flex flex-none">
                <IconAction label="Move up" disabled={index === 0} onClick={() => move(index, index - 1)}>
                  <ArrowUp size={15} />
                </IconAction>
                <IconAction label="Move down" disabled={index === items.length - 1} onClick={() => move(index, index + 1)}>
                  <ArrowDown size={15} />
                </IconAction>
                <IconAction label="Remove from the carousel" onClick={() => onChange(items.filter((_, i) => i !== index))}>
                  <X size={15} />
                </IconAction>
              </div>
            </li>
          );
        })}
      </ol>
      <div className="mt-3 flex shrink-0 items-center justify-between gap-2 border-t border-hazumu-border pt-3">
        <Button variant="secondary" size="sm" onClick={onAdd} disabled={items.length >= 24}>
          <Plus size={14} aria-hidden="true" />
          Add images
        </Button>
        <Button variant="primary" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>
    </BottomSheet>,
    document.body,
  );
}

const IconAction = ({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: ReactNode }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="grid size-8 place-items-center rounded-lg text-hazumu-ink hover:bg-hazumu-canvas focus-visible:outline-2 focus-visible:outline-hazumu-ink disabled:opacity-30"
  >
    {children}
  </button>
);
